import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { languages, SupportedLanguages } from '../i18n';
import { analyticsService } from '../lib/analytics/analyticsService';
import { useQuery } from '@tanstack/react-query';
import { configClient } from '../lib/supabase/configClient';
import { logger } from '../lib/logger';
import { sessionTracker } from '../lib/analytics/sessionTracker';

interface LanguageSwitcherProps {
  showText?: boolean;
  textColor?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  showText = false,
  textColor
}) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Load configuration to get available languages
  const { data: config } = useQuery({
    queryKey: ['config'],
    queryFn: () => configClient.getConfig(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  // Gérer le responsive
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const changeLanguage = useCallback((lng: SupportedLanguages) => {
    const currentLang = i18n.language as SupportedLanguages;
    
    analyticsService.trackEvent({
      event_type: 'user',
      event_name: 'language_change',
      event_data: {
        from_language: currentLang,
        to_language: lng
      }
    });

    i18n.changeLanguage(lng);
    setIsOpen(false);

    // Update session language in the tracker
    sessionTracker.updateLanguage(lng);

    
  }, [i18n]);

  // Get current language name
  const currentLanguage = languages[i18n.language.split('-')[0] as SupportedLanguages] || languages.fr;

  // Get available languages from config, ensuring it's an array
  const availableLanguages = Array.isArray(config?.available_languages) 
    ? config.available_languages 
    : ['fr', 'en'];

  return (
    <div className="relative z-[9999]">
      <button
        className="p-2 hover:bg-white/10 rounded-full transition-colors flex items-center gap-2"
        style={{ color: textColor }}
        aria-label="Changer de langue"
        onClick={() => setIsOpen(!isOpen)}
      >
        {showText && !isMobile && (
          <span className="text-sm font-medium">{currentLanguage}</span>
        )}
        <Globe 
          size={20} 
          strokeWidth={1.5}
          fill="none"
          stroke="currentColor"
          style={{ color: textColor }}
        />
      </button>
      
      <div 
        className={`absolute right-0 mt-2 w-40 shadow-lg bg-white ring-1 ring-black ring-opacity-5 transition-all duration-200 z-[9999] ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        style={{ borderRadius: '0.5rem' }}
      >
        <div className="py-1">
          {availableLanguages.map(code => (
            <button
              key={code}
              onClick={() => changeLanguage(code as SupportedLanguages)}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                i18n.language.split('-')[0] === code ? 'font-semibold bg-gray-50' : ''
              }`}
            >
              {languages[code as SupportedLanguages]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};